
import {
  computed,
  defineComponent,
  nextTick,
  PropType,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import { DropdownOption, NextStepEnum } from '@/types';
import FunnelStepSelector from '@/components/template-builder/FunnelStepSelector.vue';
import TemplateSelectorV2 from '@/components/automated-scenarios/fields/TemplateSelectorV2.vue';
import VariablesButton from '@/components/fields/partials/VariablesButton.vue';
import {
  TemplateEditorState,
  FunnelStep,
  addStepToFunnel,
  addTemplateToFunnels,
  discoverAndAttachSubFunnels,
  createNextStepTemplate,
  generateNextStepName,
  prepareTemplate,
  syncGameFunnelStepsForTemplate,
  hasGameWidgetInOtherFunnelTemplates,
} from '@/composables/template-editor/TemplateEditor';
import {
  isDisplayTemplate,
  isEmailTemplate,
  getTemplateParentTypeByType,
} from '@/components/template-builder/utils/helpers';
import { UserState } from '@/composables/User';
import { showToastError } from '@/helpers';
import { store } from '@/store';
import { Get } from '@/composables/GraphQL';
import { Templates } from '@/types/generated-types/graphql';

export default defineComponent({
  name: 'WysiwygLinkDialog',

  components: {
    Dialog,
    Button,
    Dropdown,
    InputText,
    FunnelStepSelector,
    TemplateSelectorV2,
    VariablesButton,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    initialUrl: {
      type: String,
      default: '',
    },

    initialText: {
      type: String,
      default: '',
    },

    showFunnelOption: {
      type: Boolean,
      default: false,
    },

    funnelOnly: {
      type: Boolean,
      default: false,
    },

    variablesList: {
      type: Array as PropType<DropdownOption[]>,
      default: () => [],
    },
  },

  emits: ['update:visible', 'confirm', 'remove'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const urlValue = ref('');
    const textValue = ref('');
    const linkMode = ref<'url' | 'funnel'>('url');
    const loading = ref(false);
    const selectedNextStep = ref<NextStepEnum | null>(null);
    const selectedFunnelStep = ref<FunnelStep | null>(null);
    const showNextStepGrid = ref(false);
    const displayTemplateModal = ref(false);
    const urlInputRef = ref();
    const cursorPosition = ref(0);

    // Track cursor position in URL input for variable insertion
    const saveCursorPosition = () => {
      const input = urlInputRef.value?.$el || urlInputRef.value;
      if (input) {
        cursorPosition.value = input.selectionStart ?? urlValue.value.length;
      }
    };

    const insertVariable = (variable: string) => {
      const current = urlValue.value ?? '';
      const pos = cursorPosition.value;
      urlValue.value = current.substring(0, pos) + variable + current.substring(pos);
      cursorPosition.value = pos + variable.length;
    };

    // Track the currently selected redirect (for the info card)
    const selectedRedirectLabel = ref('');
    const isGameRedirect = ref(false);

    const isVisible = computed({
      get: () => props.visible,
      set: (val: boolean) => emit('update:visible', val),
    });

    const isEditing = computed(() => !!props.initialUrl);

    const isInitialRedirect = computed(() => {
      const url = props.initialUrl;
      return url.includes('#redirect_') || url.includes('#spin_redirect_');
    });

    const hasSelectedRedirect = computed(() => isInitialRedirect.value || selectedRedirectLabel.value);

    const idCampaign = computed(() => TemplateEditorState.template?.idCampaign ?? null);

    // baseLinkData — provides context for TemplateSelectorV2 to correctly
    // name and link the duplicated template (same logic as BaseLink.vue)
    const baseLinkData = computed(() => {
      const namePrimaryTemplate = TemplateEditorState?.template?.informations.name;
      const translationsPrimaryTemplate = TemplateEditorState?.template?.translations ?? [];
      const uniqueRedirects = new Set();

      translationsPrimaryTemplate.forEach((translation: any) => {
        const { value } = translation;
        const match = value.match(/#(?:spin_)?redirect_(.+)/);
        if (match) {
          try {
            const idRedirect = window.atob(match[1]);
            if (!idRedirect.match(new RegExp('[a-zA-Z]'))) {
              uniqueRedirects.add(idRedirect);
            }
          } catch (e) { /* invalid base64 */ }
        }
      });

      const countRedirectTemplate = uniqueRedirects.size;

      return {
        namePrimaryTemplate,
        countRedirectTemplate,
        originTemplateId: TemplateEditorState?.template?.id,
      };
    });

    // Initialize values when dialog opens
    watch(() => props.visible, async (val) => {
      if (val) {
        selectedNextStep.value = null;
        selectedFunnelStep.value = null;
        loading.value = false;
        showNextStepGrid.value = false;
        displayTemplateModal.value = false;
        selectedRedirectLabel.value = '';
        textValue.value = props.initialText || '';
        isGameRedirect.value = false;

        if (props.funnelOnly || isInitialRedirect.value) {
          linkMode.value = 'funnel';
          urlValue.value = '';

          if (isInitialRedirect.value) {
            // Resolve redirect label from initial URL
            const url = props.initialUrl;
            if (url.includes('game_result') || url.includes('game_prizes')) {
              isGameRedirect.value = true;
            } else {
              // Extract template ID from #redirect_{base64} or #spin_redirect_{base64}
              const parts = url.includes('#spin_redirect_')
                ? url.split('#spin_redirect_')
                : url.split('#redirect_');
              const encoded = parts[1] || '';
              if (encoded) {
                try {
                  const templateId = atob(encoded);
                  const { item } = await Get<Templates>({
                    name: 'Templates',
                    id: Number(templateId),
                    keyName: 'id_template',
                    fields: ['id_template', 'label'],
                  });
                  if (item) {
                    selectedRedirectLabel.value = item.label || `Template #${templateId}`;
                  }
                } catch {
                  selectedRedirectLabel.value = `Template`;
                }
              }
            }
          } else {
            // funnelOnly with no existing redirect — show grid immediately
            showNextStepGrid.value = true;
          }
        } else {
          linkMode.value = 'url';
          urlValue.value = props.initialUrl || '';

          nextTick(() => {
            if (urlInputRef.value) {
              urlInputRef.value.$el?.focus?.();
            }
          });
        }
      }
    });

    // Link mode options
    const linkModeOptions = computed(() => [
      {
        label: t('templateBuilder.wysiwygLinkDialog.modeUrl'),
        value: 'url',
      },
      {
        label: t('templateBuilder.wysiwygLinkDialog.modeFunnel'),
        value: 'funnel',
      },
    ]);

    const handleLinkModeChange = () => {
      if (linkMode.value === 'funnel') {
        // If no existing redirect, show grid immediately
        if (!hasSelectedRedirect.value) {
          showNextStepGrid.value = true;
        }
      }
    };

    // Funnel steps
    const funnelSteps = computed(() => (
      TemplateEditorState.template ? TemplateEditorState.template.funnel.nextSteps : []
    ));

    // Pass raw funnel steps to FunnelStepSelector — it handles its own filtering
    // and adds the "Game result" option when game steps exist
    const allFunnelSteps = computed(() => {
      const steps = funnelSteps.value;
      // Convert readonly to mutable for FunnelStepSelector compatibility
      return (steps as any[]).map((step: any) => ({
        template: step.template,
        nextSteps: step.nextSteps || [],
      })) as FunnelStep[];
    });

    // Next step options — same logic as BaseLink.vue
    const nextStepIconMap: Record<string, string> = {
      [NextStepEnum.COLLECT_PERSONAL_INFO]: 'fa-light fa-address-card',
      [NextStepEnum.COLLECT_PHONE_NUMBER]: 'fa-light fa-phone',
      [NextStepEnum.OFFER_VOUCHER]: 'fa-light fa-gift',
      [NextStepEnum.EMPTY_STEP]: 'fa-light fa-file',
      [NextStepEnum.CHOOSE_EXISTING_NEXT_STEP]: 'fa-light fa-link',
      [NextStepEnum.DISPLAY_POPUP]: 'fa-light fa-palette',
    };

    const nextStepOptions = computed(() => {
      let stepOptions: NextStepEnum[];
      if (isDisplayTemplate(TemplateEditorState.template?.type)) {
        stepOptions = [
          NextStepEnum.COLLECT_PERSONAL_INFO,
          NextStepEnum.COLLECT_PHONE_NUMBER,
          NextStepEnum.OFFER_VOUCHER,
          NextStepEnum.EMPTY_STEP,
          NextStepEnum.CHOOSE_EXISTING_NEXT_STEP,
          NextStepEnum.DISPLAY_POPUP,
        ];
      } else {
        // Email templates: only gallery and existing step
        stepOptions = [
          NextStepEnum.DISPLAY_POPUP,
          NextStepEnum.CHOOSE_EXISTING_NEXT_STEP,
        ];
      }

      return stepOptions.map((option) => {
        const isDisplayPopupRenamedAsNew = isDisplayTemplate(TemplateEditorState.template?.type) && option === NextStepEnum.DISPLAY_POPUP;
        const labelKey = isDisplayPopupRenamedAsNew ? 'chooseNewTemplate' : option;
        return {
          label: t(`templateBuilder.modals.nextStepModal.steps.${labelKey}.title`),
          subtitle: t(`templateBuilder.modals.nextStepModal.steps.${labelKey}.subtitle`),
          icon: nextStepIconMap[option] || 'fa-light fa-circle',
          value: option,
          disabled: option === NextStepEnum.CHOOSE_EXISTING_NEXT_STEP && !allFunnelSteps.value.length && !hasGameWidgetInOtherFunnelTemplates(),
        };
      });
    });

    // ---- URL Validation ----

    const normalizeUrl = (url: string): string => {
      let normalized = url.trim();

      if (normalized.startsWith('{var=') || normalized.startsWith('{var =')) {
        return normalized;
      }

      const doubleProtocolMatch = normalized.match(/^(https?:\/\/)(https?:\/\/)/i);
      if (doubleProtocolMatch) {
        normalized = normalized.substring(doubleProtocolMatch[1].length);
      }

      if (normalized && !normalized.match(/^(https?:\/\/|mailto:|tel:|#|\{var)/i)) {
        normalized = `https://${normalized}`;
      }

      return normalized;
    };

    const urlError = computed(() => {
      const url = urlValue.value.trim();
      if (!url) return '';
      if (url === 'http://' || url === 'https://') {
        return t('templateBuilder.wysiwygLinkDialog.errors.emptyUrl');
      }
      return '';
    });

    const urlWarning = computed(() => {
      const url = urlValue.value.trim();
      if (!url) return '';

      const hasProtocol = /^https?:\/\//i.test(url);
      if (hasProtocol) {
        const afterProtocol = url.replace(/^https?:\/\//i, '');
        if (afterProtocol.startsWith('{var=')) {
          return t('templateBuilder.wysiwygLinkDialog.warnings.doubleProtocol');
        }
      }

      if (!url.match(/^(https?:\/\/|mailto:|tel:|#|\{var)/i)) {
        return t('templateBuilder.wysiwygLinkDialog.warnings.noProtocol');
      }

      return '';
    });

    // ---- Emit helper ----

    const emitFunnelResult = (href: string) => {
      emit('confirm', { type: 'funnel', url: href, text: textValue.value });
      showNextStepGrid.value = false;
      isVisible.value = false;
    };

    // ---- Handle CHOOSE_EXISTING_NEXT_STEP ----

    const handleExistingStep = async () => {
      if (!selectedFunnelStep.value) {
        showToastError(t('templateBuilder.funnelStepSelector.error'));
        return;
      }

      // Game result
      if (selectedFunnelStep.value.template?.isGameResult) {
        emitFunnelResult('#redirect_game_result');
        return;
      }

      const templateId = selectedFunnelStep.value.template?.id;
      if (templateId) {
        addStepToFunnel(selectedFunnelStep.value);
        emitFunnelResult(`#redirect_${btoa(String(templateId))}`);
      }
    };

    // ---- Handle DISPLAY_POPUP (opens TemplateSelectorV2) ----

    const handleChooseTemplate = async (idTemplate: number) => {
      loading.value = true;
      store.commit('general/showTheSpinner');

      const { item } = await Get<Templates>({
        name: 'Templates',
        id: idTemplate,
        keyName: 'id_template',
        fields: ['id_template', 'label', 'type'],
      });

      if (item) {
        const preparedTemplate = await prepareTemplate(idTemplate, item.label, item.type as string);
        if (preparedTemplate) {
          addTemplateToFunnels(preparedTemplate.template);
          await discoverAndAttachSubFunnels(preparedTemplate.template);
          syncGameFunnelStepsForTemplate(preparedTemplate.template.id);
        }

        displayTemplateModal.value = false;
        store.commit('general/hideTheSpinner');
        loading.value = false;
        emitFunnelResult(`#redirect_${btoa(String(idTemplate))}`);
      } else {
        showToastError(t('errorMessages.GENERIC_ERROR'));
        store.commit('general/hideTheSpinner');
        loading.value = false;
      }
    };

    // ---- Handle template creation steps (COLLECT_PERSONAL_INFO, EMPTY_STEP, etc.) ----

    const handleCreateStep = async () => {
      if (!selectedNextStep.value) return;

      loading.value = true;
      store.commit('general/showTheSpinner');

      const idShop = UserState.activeShop ? UserState.activeShop.id : 0;
      const result = await createNextStepTemplate(
        idShop,
        selectedNextStep.value,
        generateNextStepName(
          TemplateEditorState.template?.informations.name || '',
          getTemplateParentTypeByType(TemplateEditorState.template?.type),
          t,
        ),
        t,
        idCampaign.value,
        'templateBuilder',
      );

      if (!result) {
        showToastError(t('errorMessages.GENERIC_ERROR'));
        store.commit('general/hideTheSpinner');
        loading.value = false;
        return;
      }

      const { template } = result;
      if (template) {
        addTemplateToFunnels(template);
        await discoverAndAttachSubFunnels(template);
      }

      store.commit('general/hideTheSpinner');
      loading.value = false;
      emitFunnelResult(`#redirect_${btoa(String(template.id))}`);
    };

    // ---- Main Confirm ----

    const canConfirm = computed(() => {
      if (linkMode.value === 'url') {
        return urlValue.value.trim().length > 0 && !urlError.value;
      }
      if (linkMode.value === 'funnel') {
        // If showing info card (existing redirect), confirm keeps it
        if (hasSelectedRedirect.value && !showNextStepGrid.value) {
          return true;
        }
        // Grid is shown: need a selection
        if (!selectedNextStep.value) return false;
        if (selectedNextStep.value === NextStepEnum.CHOOSE_EXISTING_NEXT_STEP) {
          return !!selectedFunnelStep.value;
        }
        return true;
      }
      return false;
    });

    const handleConfirm = async () => {
      if (!canConfirm.value) return;

      if (linkMode.value === 'url') {
        const finalUrl = normalizeUrl(urlValue.value);
        emit('confirm', { type: 'url', url: finalUrl, text: textValue.value });
        isVisible.value = false;
        return;
      }

      if (linkMode.value === 'funnel') {
        // If existing redirect and grid not shown, keep current value
        if (hasSelectedRedirect.value && !showNextStepGrid.value) {
          // No change, just close
          isVisible.value = false;
          return;
        }

        if (selectedNextStep.value === NextStepEnum.CHOOSE_EXISTING_NEXT_STEP) {
          await handleExistingStep();
        } else if (selectedNextStep.value === NextStepEnum.DISPLAY_POPUP) {
          displayTemplateModal.value = true;
        } else {
          // COLLECT_PERSONAL_INFO, COLLECT_PHONE_NUMBER, OFFER_VOUCHER, EMPTY_STEP
          await handleCreateStep();
        }
      }
    };

    const handleCancel = () => {
      displayTemplateModal.value = false;
      isVisible.value = false;
    };

    const handleRemoveLink = () => {
      emit('remove');
      displayTemplateModal.value = false;
      isVisible.value = false;
    };

    return {
      t,
      isVisible,
      isEditing,
      linkMode,
      linkModeOptions,
      urlValue,
      textValue,
      urlInputRef,
      saveCursorPosition,
      insertVariable,
      urlError,
      urlWarning,
      loading,
      selectedNextStep,
      selectedFunnelStep,
      allFunnelSteps,
      nextStepOptions,
      showNextStepGrid,
      hasSelectedRedirect,
      selectedRedirectLabel,
      isGameRedirect,
      displayTemplateModal,
      idCampaign,
      baseLinkData,
      canConfirm,
      handleConfirm,
      handleCancel,
      handleRemoveLink,
      handleChooseTemplate,
      handleLinkModeChange,
      NextStepEnum,
    };
  },
});
